import type { FC } from "react";
import { useMemo, useState } from "react";

import { Table } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import { getColumnConfig } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/LLMTable/config";
import { TableWrapper } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/styledComponents";
import type {
    DropdownOnClickProps,
    IAiTrackerTableRow,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { allTrackers } from "app/services/track/track";

import type { SortDirection } from "./types";

interface ILLMTableProps {
    data: IAiTrackerTableRow[];
    onDropdownClick: (props: DropdownOnClickProps, row: IAiTrackerTableRow) => void;
    onCampaignSelection: (row: IAiTrackerTableRow) => void;
    onLockedCampaignSelection: () => void;
    isSubscribePending?: boolean;
}

export const LLMTable: FC<ILLMTableProps> = ({
    data,
    onDropdownClick,
    onCampaignSelection,
    onLockedCampaignSelection,
    isSubscribePending,
}) => {
    const [sorting, setSorting] = useState<{
        dataIndex: keyof IAiTrackerTableRow;
        sortDirection: SortDirection;
    }>({
        dataIndex: "createdDate",
        sortDirection: "descend",
    });

    const config = useMemo(() => {
        return getColumnConfig(data.length, onDropdownClick, sorting, isSubscribePending);
    }, [onDropdownClick, data, sorting, isSubscribePending]);

    const handleSortBy = (dataIndex: keyof IAiTrackerTableRow, sortDirection: SortDirection) => {
        setSorting({ dataIndex, sortDirection });
        allTrackers.dispatchTrackEvent("Sort", "Click", dataIndex);
    };

    return (
        <TableWrapper>
            <Table
                key={`ai-tracker-table-${data.length}`}
                id="ai-tracker-table"
                size="large"
                pagination={{ pageSize: 100 }}
                onSort={(sorterData) => {
                    handleSortBy(sorterData.columnKey as keyof IAiTrackerTableRow, sorterData.order);
                }}
                sticky={false}
                columnConfig={config}
                data={data}
                theme="carbon"
                onRow={(record) => ({
                    onClick: () => {
                        if (record.isLocked) {
                            onLockedCampaignSelection?.();
                        } else {
                            onCampaignSelection?.(record);
                        }
                    },
                })}
                noData={{
                    title: translate("llm.homepage.table.no_data.title"),
                    subtitle: translate("llm.homepage.table.no_data.subtitle"),
                }}
            />
        </TableWrapper>
    );
};
